/**
 * Client-side audio extraction for Provider A-Roll transcription.
 *
 * Why this exists: the server-side `/api/provider-transcribe` endpoint used
 * to accept the full source video (multi-GB), stash it in tmp, then run
 * ffmpeg to pull out a tiny 16 kHz mono mp3 for Whisper. Uploading a 15 GB
 * video over a single HTTP request is fragile — proxy timeouts and any
 * network blip kill the whole job. Extracting the audio in the browser
 * first turns that 15 GB upload into ~100 MB.
 *
 * Implementation notes:
 *   - ffmpeg.wasm is lazy-loaded the first time `extractAudio` is called.
 *     The 31 MB core.wasm download is one-time and browser-cached.
 *   - We self-host the core files in `public/ffmpeg/` to avoid runtime
 *     CDN dependencies (some clinical networks block unpkg/jsdelivr).
 *   - CRITICAL: the user's File is mounted via WORKERFS rather than copied
 *     into the wasm heap with `writeFile`. `writeFile`/`fetchFile` would
 *     allocate a single Uint8Array the size of the source video — for a
 *     3-15 GB clip that exceeds Chrome's per-tab heap limit, throws, and
 *     silently drops us into the legacy whole-video upload path. WORKERFS
 *     wraps the underlying Blob and lazy-reads chunks as ffmpeg requests
 *     them, so memory usage stays flat regardless of source size.
 *   - Output args mirror the server's existing extraction exactly:
 *     `-vn -ac 1 -ar 16000 -b:a 64k -codec:a libmp3lame -f mp3` — so
 *     downstream chunking + Whisper invocation behave identically.
 */
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { toBlobURL } from "@ffmpeg/util";

/**
 * Files at/above this size reliably crash extraction with a wasm-level
 * `RuntimeError: memory access out of bounds` trap rather than a catchable
 * ffmpeg error. The bundled `@ffmpeg/core` is a single-threaded wasm32
 * build — a 32-bit address space — and its internal seek/offset math (used
 * by both the MOV/MP4 demuxer's index parsing and the WORKERFS lazy-read
 * path, see the WORKERFS note above) overflows once a source video's
 * internal byte offsets climb past roughly 2 GB. There's no runtime knob
 * to raise this from the @ffmpeg/ffmpeg JS API — it's baked into the wasm
 * build. Callers should check `file.size` against this constant *before*
 * calling `extractAudioForTranscription` and skip straight to the
 * whole-video upload fallback for anything at/above it, rather than paying
 * for a doomed extraction attempt.
 */
export const MAX_CLIENT_EXTRACTION_BYTES = 2 * 1024 ** 3; // 2 GB

/** Singleton FFmpeg instance — created lazily on first extraction. */
let ffmpegPromise: Promise<FFmpeg> | null = null;

/** Resolved absolute URLs for the self-hosted core files (computed once). */
function coreBaseUrl(): string {
  // `import.meta.env.BASE_URL` honors any Vite base path configured for
  // sub-path deployments; falls back to "/" in the dev server case.
  const base =
    typeof import.meta !== "undefined" && (import.meta as any).env
      ? ((import.meta as any).env.BASE_URL as string) || "/"
      : "/";
  return base.endsWith("/") ? `${base}ffmpeg` : `${base}/ffmpeg`;
}

async function loadFFmpeg(): Promise<FFmpeg> {
  if (!ffmpegPromise) {
    ffmpegPromise = (async () => {
      const ff = new FFmpeg();
      const base = coreBaseUrl();
      // Surface ffmpeg's own log output to the browser console — invaluable
      // when an extraction fails on a user's machine and we need to see
      // what the wasm side actually said.
      ff.on("log", ({ message }) => {
        // Only forward stderr-ish messages; ffmpeg is very chatty.
        if (message) console.debug("[ffmpeg.wasm]", message);
      });
      // toBlobURL fetches the self-hosted core files from /ffmpeg/ and
      // re-exposes them as blob: URLs with explicit MIME types. This is
      // the @ffmpeg/ffmpeg-documented workaround for the worker's
      // importScripts/import path: a Vite-bundled module worker can hit
      // CORS / MIME / module-format issues importing a same-origin .js
      // directly, but loading from a blob: URL with the right type works
      // consistently. The downloads still come from our own server, so
      // we keep the no-CDN guarantee.
      console.log("[audioExtractor] loading ffmpeg.wasm core from", base);
      const [coreURL, wasmURL] = await Promise.all([
        toBlobURL(`${base}/ffmpeg-core.js`, "text/javascript"),
        toBlobURL(`${base}/ffmpeg-core.wasm`, "application/wasm"),
      ]);
      await ff.load({ coreURL, wasmURL });
      console.log("[audioExtractor] ffmpeg.wasm core loaded");
      return ff;
    })().catch((err) => {
      // Don't cache the failed promise — the next caller should retry.
      ffmpegPromise = null;
      throw err;
    });
  }
  return ffmpegPromise;
}

/**
 * Sanitize a filename for use inside ffmpeg.wasm's virtual filesystem. The
 * WORKERFS layer is permissive but flaky around exotic characters
 * (Unicode, leading dots, etc.), and we don't need the original name
 * preserved — only the extension matters for ffmpeg's demuxer auto-probe.
 */
function safeVfsName(name: string): string {
  const ext = (name.match(/\.([a-zA-Z0-9]+)$/)?.[1] ?? "bin").toLowerCase();
  return `input.${ext}`;
}

const MOUNT_DIR = "/source";
const OUTPUT_NAME = "output.mp3";

/**
 * Extract a Whisper-friendly mp3 audio track from a browser File.
 *
 * @param file       The user's source video (or audio) File.
 * @param onProgress Optional callback fired with extraction progress (0-100).
 *                   ffmpeg.wasm's progress event reports a normalized ratio,
 *                   which we convert to an integer percentage.
 *
 * Throws on ffmpeg failure (missing wasm, unsupported codec, etc.) so the
 * caller can fall back to the legacy whole-video upload path.
 */
export async function extractAudioForTranscription(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<Blob> {
  console.log(
    `[audioExtractor] start ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)`,
  );
  const ffmpeg = await loadFFmpeg();

  const inputName = safeVfsName(file.name);

  const progressHandler = (event: { progress: number; time: number }) => {
    if (!onProgress) return;
    const pct = Math.round(Math.min(1, Math.max(0, event.progress)) * 100);
    onProgress(pct);
  };
  ffmpeg.on("progress", progressHandler);

  let mounted = false;
  try {
    // Make sure the mount point exists (createDir throws if it already
    // does, so we swallow). Then mount the File via WORKERFS — ffmpeg
    // reads lazily from the underlying Blob, no full-file heap copy.
    try {
      await ffmpeg.createDir(MOUNT_DIR);
    } catch {
      // Directory already exists from a prior extraction — fine.
    }
    // The `blobs` option lets us mount with a fixed name regardless of
    // the actual file.name (which may have spaces / Unicode / odd cases
    // that occasionally confuse ffmpeg's demuxer path handling).
    // Cast through unknown because @ffmpeg/ffmpeg's published types are
    // narrower than the runtime's actual WORKERFS support.
    await (ffmpeg as unknown as {
      mount: (
        fs: string,
        opts: { blobs: Array<{ name: string; data: Blob }> },
        path: string,
      ) => Promise<boolean>;
    }).mount(
      "WORKERFS",
      { blobs: [{ name: inputName, data: file }] },
      MOUNT_DIR,
    );
    mounted = true;
    console.log(`[audioExtractor] mounted ${inputName}, running ffmpeg`);

    const inputPath = `${MOUNT_DIR}/${inputName}`;
    // Same args as `extractAudioChunks` in src/server/bedrock.ts, minus the
    // segment splitting (the server still chunks for Whisper's 25 MB body
    // cap — we just hand it a much smaller starting point).
    const exitCode = await ffmpeg.exec([
      "-y",
      "-i", inputPath,
      "-vn",
      "-ac", "1",
      "-ar", "16000",
      "-b:a", "64k",
      "-codec:a", "libmp3lame",
      "-f", "mp3",
      OUTPUT_NAME,
    ]);
    if (exitCode !== 0) {
      throw new Error(`ffmpeg.wasm exited with code ${exitCode}`);
    }
    console.log(`[audioExtractor] ffmpeg done (exit ${exitCode})`);

    const data = await ffmpeg.readFile(OUTPUT_NAME);
    // readFile returns Uint8Array (or string when explicitly requested);
    // wrap as a Blob with a clear mime type so the network layer + the
    // server's busboy parser route it sensibly. We copy into a fresh
    // ArrayBuffer so the Blob never references a SharedArrayBuffer (which
    // breaks TS's tightened BlobPart type and would also fail in some
    // structured-clone scenarios).
    const source = typeof data === "string" ? new TextEncoder().encode(data) : data;
    const arrayBuffer = new ArrayBuffer(source.byteLength);
    new Uint8Array(arrayBuffer).set(source);
    console.log(
      `[audioExtractor] audio ready (${(arrayBuffer.byteLength / 1024 / 1024).toFixed(1)} MB)`,
    );
    return new Blob([arrayBuffer], { type: "audio/mpeg" });
  } finally {
    ffmpeg.off("progress", progressHandler);
    if (mounted) {
      // Cast through unknown because @ffmpeg/ffmpeg's published types
      // don't expose unmount at the moment.
      await (ffmpeg as unknown as { unmount: (p: string) => Promise<boolean> })
        .unmount(MOUNT_DIR)
        .catch(() => {});
    }
    await ffmpeg.deleteFile(OUTPUT_NAME).catch(() => {});
  }
}

/**
 * Best-effort pre-warm of ffmpeg.wasm so the first extraction doesn't pay
 * the 31 MB core download as part of its critical path. Safe to call
 * multiple times — it dedupes on the singleton promise.
 */
export function prewarmFFmpeg(): void {
  void loadFFmpeg().catch(() => {
    // Swallowing here is fine — `extractAudioForTranscription` will
    // surface a clean error on the actual call.
  });
}
